import { useEffect, useRef } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAtomValue } from 'jotai';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RoutePath } from '@/routes/RoutePath';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';

import { getCareSettingTerminologyForFacilityType } from './careSettingTerminology';
import { allFacilitiesIntentAtom } from './useScreenerFacilityView';

export const useWarnOnLeavingAllFacilitiesSupportedRoute = ({
  pathname,
  isInAllFacilitiesView,
  selectedFacilityType
}: {
  pathname: string;
  isInAllFacilitiesView: boolean;
  selectedFacilityType: FacilityFacilityTypeEnum;
}) => {
  const { showSnackbar } = useSnackbar();
  const intent = useAtomValue(allFacilitiesIntentAtom);
  const { isLoading: isCurrentUserLoading } = useCurrentUser();
  const hasWarnedRef = useRef(false);
  const allFacilitiesCareSettingTerms =
    getCareSettingTerminologyForFacilityType(intent?.facilityType);
  const selectedFacilityCareSettingTerms =
    getCareSettingTerminologyForFacilityType(selectedFacilityType);

  const isOnSingleLeadView = pathname.startsWith(
    `${RoutePath.CRMScreenerAllLeads}/`
  );

  const shouldWarn =
    !isCurrentUserLoading &&
    !!intent &&
    !isInAllFacilitiesView &&
    !isOnSingleLeadView;

  useEffect(() => {
    if (shouldWarn && !hasWarnedRef.current) {
      hasWarnedRef.current = true;
      showSnackbar({
        AlertProps: {
          icon: (
            <ErrorOutlineIcon
              sx={{ color: 'warning' }}
              className="MuiAlert-icon"
            />
          )
        },
        severity: 'warning',
        message: `All ${allFacilitiesCareSettingTerms.pluralTitle} view is not enabled for this page, your last viewed ${selectedFacilityCareSettingTerms.singularLower} has been selected.`,
        NotistackOptions: {
          key: 'leaving-all-facilities-route-warning',
          preventDuplicate: true,
          autoHideDuration: 1500
        }
      });
    } else if (!shouldWarn) {
      hasWarnedRef.current = false;
    }
  }, [
    allFacilitiesCareSettingTerms.pluralTitle,
    selectedFacilityCareSettingTerms.singularLower,
    shouldWarn,
    showSnackbar
  ]);
};
